import TopBar from 'components/menu/topBar.component';
import { HomeLeftBar } from 'components/menu/homeLeftBar.component';
import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from 'services/navigation.service';
import { CommonSectorService, HeaderNavigateBar, LoggerService, RootState, usePersistantState, UUID } from '@umanforge/common';
import React, { useEffect, useMemo, useState } from 'react';
import Footer from './footer.component';
import { Loading } from '@common_components/loading.component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { KEY } from 'constants/local_storage_key.constant';
import { AxiosResponse, AxiosError } from 'axios';
import { GlobalService } from 'services/global.service';
import { ErrorIcons } from 'components/other/_ErrorIcons';

interface PageProps {
  children: JSX.Element;
  headerChildren?: JSX.Element;
  headerNavigateBarChildren?: JSX.Element;
  title?: string;
  needConfirmation?: boolean;
  footer?: boolean;
  bar?: boolean;
  isAdminRequired?: boolean
  onSectorChange?: ( sectorId: string | undefined ) => void
  setIsAdmin?: ( isAdmin: boolean ) => void
}

const Page: React.FC<PageProps> = ( {
  children,
  headerChildren,
  headerNavigateBarChildren,
  title,
  needConfirmation = false,
  footer = true,
  bar = true,
  isAdminRequired = false,
  onSectorChange = () => { return; },
  setIsAdmin = () => { return; }
} ) => {
  const me = useSelector( ( state: RootState ) => {return state.user.value;} );
  const [ clientId, setClientId ] = usePersistantState< UUID | undefined >( KEY.SELECTED_CLIENT_ID, undefined );
  const [ selectedSectorId, setSelectedSector ] = usePersistantState<string | undefined>( KEY.SELECTED_SECTOR_ID, undefined );
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [ isAdmin, _setIsAdmin ] = useState( false );
  const [ isFirstRender, setIsFirstRender ] = useState( true );
  const [ updateHeader, setUpdateHeader ] = useState<number>( 0 );

  const logger: LoggerService = new LoggerService( 'Page component' );
  const navigate = useNavigate();

  const refreshHeader = () => {
    setUpdateHeader( updateHeader + 1 );
  };

  const refreshClient = () => {
    const client = me?.clients[ 0 ];
    const site = ( client?.sites ?? [] )[ 0 ];
    const sector = ( site?.sectors ?? [] )[ 0 ];

    if ( client && sector ) {
      setClientId( client.id );
      setSelectedSector( sector.id );
    }
    else {
      logger.error( `Error update client -> client: ${client}, sector: ${sector}` );
    }
  };

  const onAxiosWarning = ( warn : AxiosResponse ) => {
    GlobalService.onAxiosWarning( warn );
  };

  const onAxiosError = ( err : AxiosError ) => {
    GlobalService.onAxiosError( err, dispatch );
  };

  const fetchPermission = async () => {
    if ( selectedSectorId ) {
      try {
        await CommonSectorService.getSector( selectedSectorId, token, dispatch, undefined, error => {
          logger.error( `Error fetching permission for sector ${selectedSectorId}: ${error}` );
          if ( error.status == 403 ) {
            refreshClient();
            return;
          }
        } );
      }
      catch ( err ) {
        logger.error( `Error fetching permission for sector ${selectedSectorId}: ${err}` );
      }

      if ( me ) {
        const _isAdmin = GlobalService.isAdminOfSector( me, selectedSectorId );
        _setIsAdmin( _isAdmin );
        setIsAdmin( _isAdmin );

        if ( selectedSectorId && isAdminRequired ) {
          if ( !( _isAdmin ) ) {
            NavigationService.toHome( navigate, false );
          }
        }
      }
      else {
        _setIsAdmin( false );
        setIsAdmin( false );
        NavigationService.toHome( navigate, false );
      }
    }
    else {
      refreshClient();
    }
  };

  useEffect( () => {
    if ( !isFirstRender ) {
      fetchPermission();
    }
  }, [ isFirstRender, selectedSectorId, token ] );

  useEffect( () => {
    refreshHeader();
    onSectorChange( selectedSectorId );
  }, [ selectedSectorId ] );

  useEffect( () => {
    refreshHeader();
  }, [ token ] );

  useEffect( () => {
    setIsFirstRender( false );
  }, [ ] );

  const navBarItems = useMemo( () => {
    if ( !me ) {
      return [];
    }

    const mapSectors = ( sectors: Array<{ id: string; isAdmin?: boolean }> ) => {
      return sectors.map( sector => {
        return {
          id: sector.id,
          isAdmin: sector.isAdmin || false,
        };
      } );
    };

    const mapSites = ( sites: Array<{ id: string; isAdmin?: boolean; sectors?: Array<{ id: string; isAdmin?: boolean }> }> ) => {
      return sites.map( site => {
        return {
          id: site.id,
          isAdmin: site.isAdmin || false,
          sectors: mapSectors( site.sectors || [] ),
        };
      } );
    };

    const mapClients = ( clients: Array<{ id: string; isAdmin?: boolean; sites?: Array<{ id: string; isAdmin?: boolean; sectors?: Array<{ id: string; isAdmin?: boolean }> }> }> ) => {
      return clients.map( client => {
        return {
          id: client.id,
          isAdmin: client.isAdmin || false,
          sites: mapSites( client.sites || [] ),
        };
      } );
    };

    const user = {
      isSuperAdmin: me.isSuperAdmin || false,
      isAdminOfUser: false, // Default to false if not present
      clients: mapClients( me.clients || [] ),
    };

    return NavigationService.getNavBarItems(
      token,
      dispatch,
      t,
      user,
      localStorage.getItem( KEY.SELECTED_CLIENT_ID ) || '',
      localStorage.getItem( KEY.SELECTED_SITE_ID ) || '',
      selectedSectorId || ''
    );
  }, [ me, selectedSectorId, clientId, updateHeader ] );

  const handleSectorChange = ( sectorId: string | undefined ) => {
    setSelectedSector( sectorId );
    onSectorChange( sectorId );
  };

  return token  ? (
    <div className='flex min-h-screen w-full bg-white-base text-black'>
      <div className='flex w-full'>
        <HomeLeftBar items={ navBarItems } needConfirmation={ needConfirmation } />
        { ( !isAdminRequired || isAdmin ) ?
          <div className='w-full pr-8 ml-20 h-full whitespace-nowrap'>
            { title && (
              bar ?
                <TopBar title={title} /> :
                <div className="flex-center py-3">
                  <div className="flex items-center w-full">
                    <span className="font-comfortaa text-3xl mr-4">{ title }</span>
                  </div>
                </div>
            ) }
            { headerChildren }
            <HeaderNavigateBar
              selectedClientIdKey={ KEY.SELECTED_CLIENT_ID }
              selectedSiteIdKey={ KEY.SELECTED_SITE_ID }
              selectedSectorIdKey={ KEY.SELECTED_SECTOR_ID }
              key={ updateHeader }
              onSectorChanged={ handleSectorChange }
              onWarning={ onAxiosWarning }
              onError={ onAxiosError }
            >
              { headerNavigateBarChildren }
            </HeaderNavigateBar>
            { children }

            {/* space for the footer */}
            { footer && <div className='h-16'/>}
          </div>
          :
          <div className="flex flex-col items-center justify-center h-64 w-full text-gray-500">
            <span className="w-16 h-16 mb-4 flex items-center justify-center">{ <ErrorIcons error={undefined} size={8}/> }</span>
            <h3 className="text-lg font-medium mb-2">{ t( 'translation:no_calendar' ) }</h3>
          </div>
        }
      </div>
      { footer && <Footer/> }
    </div>
  )
    : <Loading/>;
};

export default Page;